// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { LinkedSystem } from '../misc/LinkedSystem';
import { FilledOrderItem } from '../cache/FilledOrderItem';
import { ColouringModes } from '../elements/QuickTable/QuickTableColumn';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { MessageBoxType } from '@shared/utils/MessageBoxUtils';
import { TerceraMessageBox } from '../screen/TerceraMessageBox';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { DynProperty } from '@shared/commons/DynProperty';
import { ReportType } from '@shared/commons/cache/AllowedReport';
import { DataCache } from '@shared/commons/DataCache';
import { ApplicationInfo } from '@shared/commons/ApplicationInfo';
import FilledOrdersPanelTemplate from '@front/templates/FilledOrdersPanelTemplate.html';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { ReportManager } from '@shared/utils/Managers/ReportManager';
import { FilledOrderGroupRowCalculator } from '../elements/QuickTable/GroupRows/FilledOrderAggregateCalculator';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class FilledOrdersPanel extends ApplicationPanelWithTable {
    constructor() {
        super(...arguments);
        this.headerLocaleKey = 'panel.trades';
        this.moreThanOneTTKey = 'panel.orders.menu.MoreOneSymbolInfoDisabled.tt'; // #95439
        this.noOneTTKey = 'panel.orders.menu.NoSymbolInfoDisabled.tt';
        this.rangeSelectPanel = null;
    }
    getType() {
        return PanelNames.FilledOrdersPanel;
    }
    oncomplete() {
        var _a;
        super.oncomplete();
        this.rangeSelectPanel = this.Controls.filledOrdersRangeSelect;
        this.rangeSelectPanel.getReportEvent.Subscribe(this.getFilledOrdersReport, this);
        this.rangeSelectPanel.updateSelectedRange();
        this.populateTableContextMenu();
        this.getQuickTable().OnSelectionChanged.Subscribe(this.selectionChange, this);
        DataCache.OnAddNewTradeEvent.Subscribe(this.AddTradeEvent, this);
        DataCache.OnUpdateTradeEvent.Subscribe(this.UpdateTradeEvent, this);
        DataCache.OnRemovedTradeEvent.Subscribe(this.RemovedTradeEvent, this);
        this.UpdateShowTotalsStateChange(!!this.InitShowTotals);
        if ((_a = this.menuTagDict) === null || _a === void 0 ? void 0 : _a.ShowTotals) {
            this.menuTagDict.ShowTotals.checked = !!this.InitShowTotals;
        }
    }
    dispose() {
        this.getQuickTable().OnSelectionChanged.UnSubscribe(this.selectionChange, this);
        this.rangeSelectPanel.getReportEvent.UnSubscribe(this.getFilledOrdersReport, this);
        DataCache.OnAddNewTradeEvent.UnSubscribe(this.AddTradeEvent, this);
        DataCache.OnUpdateTradeEvent.UnSubscribe(this.UpdateTradeEvent, this);
        DataCache.OnRemovedTradeEvent.UnSubscribe(this.RemovedTradeEvent, this);
        super.dispose();
    }
    jbInit() {
        super.jbInit();
        this.quickTableRactive.quickTable.groupRowsCalculator = new FilledOrderGroupRowCalculator();
    }
    populateItemsDirect() {
        const trades = DataCache.newTradesDict;
        const keys = Object.keys(trades);
        const length = keys.length;
        for (let i = 0; i < length; i++) {
            this.getQuickTable().AddItem(new FilledOrderItem(trades[keys[i]]));
        }
    }
    populateTableContextMenu() {
        const items = [];
        this.AddExportContextMenuItem(items);
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'screen.reports.print',
            visible: !Resources.isHidden('panel.trades.print'),
            event: this.printTable.bind(this)
        });
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'panel.positions.menu.View',
            svgImage: 'View',
            visible: !Resources.isHidden('contextMenu.Totals.visibility'),
            subitems: [
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.positions.menu.ShowTotals',
                    checked: false,
                    canCheck: true,
                    event: this.ShowTotalsStateChange.bind(this)
                }
            ]
        });
        this.AddSymbolInfoContextMenuItemIfNeed(items);
        this.getQuickTable().setTableContextMenuItems(items);
        this.menuTagDict = createTagDictionary(items);
    }
    AddTradeEvent(dc, trade) {
        this.getQuickTable().AddItem(new FilledOrderItem(trade));
    }
    UpdateTradeEvent(trade) {
        const qt = this.getQuickTable();
        if (!qt)
            return;
        const row = qt.rowsArray.find((row) => { var _a, _b; return ((_b = (_a = row.item) === null || _a === void 0 ? void 0 : _a.trade) === null || _b === void 0 ? void 0 : _b.TradeId) === trade.TradeId; });
        if (row) {
            row.item.trade = trade;
        }
    }
    RemovedTradeEvent(openOrderId) {
        var _a;
        const qt = (_a = this.quickTableRactive) === null || _a === void 0 ? void 0 : _a.quickTable;
        if (isNullOrUndefined(qt)) {
            return;
        }
        const rows = qt.rowsArray;
        for (let i = 0; i < rows.length; i++) {
            const item = rows[i].item;
            const trade = item === null || item === void 0 ? void 0 : item.trade;
            if (!isNullOrUndefined(trade) && trade.OrderId === openOrderId) {
                qt.RemoveItem(item.ItemId);
                break;
            }
        }
    }
    getFilledOrdersReport(startTime, finishTime) {
        void this.set('loading', true);
        ReportManager.reportsByComplited.set(ReportType.REPORT_TRADES, false);
        void ReportManager.sendRequestByDate(ReportType.REPORT_TRADES, startTime, finishTime)
            .then((filledOrders) => {
            const qt = this.getQuickTable();
            if (isNullOrUndefined(qt))
                return;
            qt.ClearRows();
            for (const order of filledOrders) {
                qt.AddItem(new FilledOrderItem(order));
            }
            if (LinkedSystem.accLinkingActive) {
                const accId = LinkedSystem.getAccount();
                qt.filterByAccountNumber(accId); // отфильтруем по аккаунту позиции, если раскрылся лишний трейд
            }
        })
            .finally(() => {
            void this.set('loading', false);
        });
    }
    selectionChange() {
        const qt = this.getQuickTable();
        const selectedRowsId = qt.selectedRowIds;
        if (!isValidArray(selectedRowsId))
            return;
        let insId = '';
        // let accId = ''
        if (selectedRowsId.length > 0) {
            const row = qt.rows[selectedRowsId[0]];
            const item = !isNullOrUndefined(row) ? row.item : null;
            if (!isNullOrUndefined(item)) {
                const ins = item.GetCurrentInstrument();
                if (!isNullOrUndefined(ins)) {
                    insId = ins.GetInteriorID();
                }
                // let acc = item.GetCurrentAccount()
                // if (acc) accId = acc.BstrAccount
            }
            if (!isValidString(insId)) {
                TerceraMessageBox.Show(Resources.getResource('screen.reports.info'), Resources.getResource('general.InstrumentIsNotAvailableNow'), MessageBoxType.Info, null, null, false, true, null, { cantActionBeforeClose: true });
            }
        }
        this.symbolLink_Out(false, insId);
        // this.accountLink_Out(false, accId)
    }
    symbolLink_Out(newSubscriber, instrumentName) {
        if (isNullOrUndefined(instrumentName)) {
            return;
        }
        const color = this.get('symbolLinkValue');
        if (color !== TerceraLinkControlConstants.STATE_NONE) {
            LinkedSystem.setSymbol(color, instrumentName, newSubscriber);
        }
    }
    Properties() {
        const properties = super.Properties();
        properties.push(new DynProperty('ShowTotals', this.getQuickTable().ShowTotals, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        return properties;
    }
    callBack(newProperties) {
        super.callBack(newProperties);
        this.InitShowTotals = DynProperty.getPropValue(newProperties, 'ShowTotals');
    }
    SetColumnsDefaultDisplayIndex(table) {
        table.columns[0].displayedIndex = 9;
        table.columns[1].displayedIndex = 10;
        table.columns[2].displayedIndex = 2;
        table.columns[3].displayedIndex = 11;
        table.columns[4].displayedIndex = 3;
        table.columns[5].displayedIndex = 4;
        table.columns[6].displayedIndex = 5;
        table.columns[7].displayedIndex = 7;
        table.columns[8].displayedIndex = 6;
        table.columns[9].displayedIndex = 1;
        table.columns[10].displayedIndex = 0;
        table.columns[11].displayedIndex = 15;
        table.columns[12].displayedIndex = 16;
        table.columns[13].displayedIndex = 17;
        table.columns[14].displayedIndex = 18;
        table.columns[15].displayedIndex = 12;
        table.columns[16].displayedIndex = 13;
        table.columns[17].displayedIndex = 14;
        table.columns[18].displayedIndex = 19;
        table.columns[19].displayedIndex = 8;
    }
    SetColumnsColouringMode(table) {
        this.ProcessSetColumnsColouringMode(table, [7, 8, 19], ColouringModes.Signed);
    }
}
ApplicationPanelWithTable.extendWith(FilledOrdersPanel, {
    data: function () {
        return {
            isAccountLinkShow: true,
            isSymbolLinkShow: true,
            isShowExploreTheAppLink: ApplicationInfo.isExploreMode
        };
    },
    partials: {
        bodyPartial: FilledOrdersPanelTemplate
    },
    headerLocaleKey: 'panel.trades',
    moreThanOneTTKey: 'panel.orders.menu.MoreOneSymbolInfoDisabled.tt', // #95439
    noOneTTKey: 'panel.orders.menu.NoSymbolInfoDisabled.tt',
    rangeSelectPanel: null
});
